// Editor — the PURE edit algebra.
//
// One edit algebra, two authors (.claude/research/design/03: the agent emits the SAME
// commands the UI does; editor-general-vs-constrained.md §3.2: typed
// `EditCommand` → pure fold). This module owns the command sum and the reducer
// that folds a command over the Element AST. It NEVER touches the DOM — it maps
// one immutable `readonly Element[]` to the next.
//
// Discipline (house rules, enforced here):
//   • Distinct command per intent — each edit is its own named `{ type }`
//     variant. Toggling a choice's correctness is TWO commands
//     (MarkChoiceCorrect / UnmarkChoiceCorrect), never setCorrect(boolean).
//   • Pure / total — no I/O, no Date/random, no id minting. Fresh ids for
//     InsertElement / AddChoice are passed IN on the command, minted at the
//     impure dispatch site. `readonly` throughout; every path returns a NEW
//     array/object and mutates no input.
//   • No `as` casts — elements are narrowed by their `type` discriminant; the
//     command switch is made exhaustive by `exhaustSwitchCase`.
//   • Unknown id ⇒ no-op — a command whose target is absent returns the tree
//     unchanged. A miss is a no-op, never a throw (the command log stays total).
//
// @bilda/client imports zero @bilda/core: the AST here is the UI's structural mirror
// (types.ts), not the domain aggregate.

import { exhaustSwitchCase } from "@bilda/utils";
import type {
    Choice,
    Element,
    ElementId,
    HeadingElement,
    PendingProposal,
    ProseElement,
    ReviewOutcome,
} from "./types.js";

// === Edit commands ============================================================
// Each intent is its own named variant carrying exactly the data its fold needs.
// `EditCommand` sums them; the reducer's switch is exhaustive over the union, so
// a new command is a compile error until it has an arm.

// --- Field edits (single-valued text on one element, matched by id) ---
export type SetHeadingText = {
    readonly type: "set-heading-text";
    readonly id: ElementId;
    readonly text: string;
};
export type SetProseMarkdown = {
    readonly type: "set-prose-markdown";
    readonly id: ElementId;
    readonly markdown: string;
};
export type SetObjectiveText = {
    readonly type: "set-objective-text";
    readonly id: ElementId;
    readonly text: string;
};
export type SetMcqStem = {
    readonly type: "set-mcq-stem";
    readonly id: ElementId;
    readonly stem: string;
};
export type SetChoiceText = {
    readonly type: "set-choice-text";
    readonly mcqId: ElementId;
    readonly choiceId: ElementId;
    readonly text: string;
};
// Per-choice feedback is genuine content (the coaching line), not a flag —
// its own intent, distinct from the choice's text. Setting empty clears it.
export type SetChoiceFeedback = {
    readonly type: "set-choice-feedback";
    readonly mcqId: ElementId;
    readonly choiceId: ElementId;
    readonly feedback: string;
};

// --- Choice structure (reshapes one MCQ's choice list, matched by mcqId) ---
export type AddChoice = {
    readonly type: "add-choice";
    readonly mcqId: ElementId;
    readonly choice: Choice; // fresh id minted at the dispatch site, passed in
};
export type RemoveChoice = {
    readonly type: "remove-choice";
    readonly mcqId: ElementId;
    readonly choiceId: ElementId;
};
// Marking a choice correct and unmarking it are DISTINCT intents, not a
// setCorrect(boolean) flag (house rule). MarkChoiceCorrect resolves against the
// element's own `answerMode` content: on a "single" MCQ it clears every other
// choice's `correct` flag (exactly one key); on "multiple" it leaves the others
// untouched. That branch is on CONTENT (the invariant the element carries), not
// on a command flag — so the two intents stay two commands.
export type MarkChoiceCorrect = {
    readonly type: "mark-choice-correct";
    readonly mcqId: ElementId;
    readonly choiceId: ElementId;
};
export type UnmarkChoiceCorrect = {
    readonly type: "unmark-choice-correct";
    readonly mcqId: ElementId;
    readonly choiceId: ElementId;
};

// Reordering a choice WITHIN its MCQ. Distinct from MoveElement (which reshapes
// the top-level array): a choice lives in one MCQ's `choices` list, so its move
// is matched by `mcqId` and lands at `toIndex` in that list — the same toIndex
// semantics as MoveElement (index into the list with the moved item removed).
export type MoveChoice = {
    readonly type: "move-choice";
    readonly mcqId: ElementId;
    readonly choiceId: ElementId;
    readonly toIndex: number;
};

// --- Enumerated block attrs ---
// A heading's level is a single enumerated VALUE (1 | 2 | 3), so setting it is
// one "apply this value" command carrying the level — the analogue of
// SetHeadingText, not a meaning-swapping flag (a flag would be a boolean toggle;
// this picks one of three named sizes). One command, the value on the payload.
export type SetHeadingLevel = {
    readonly type: "set-heading-level";
    readonly id: ElementId;
    readonly level: 1 | 2 | 3;
};

// An MCQ's answer mode is a genuine MODE SWITCH — it changes what "correct"
// means (single ⇒ exactly one key; multiple ⇒ many) — so per the house rule it
// is TWO distinct intents, never a setAnswerMode(mode) flag. AllowSingleAnswer
// additionally enforces the "at most one key" invariant it implies: it keeps the
// FIRST correct choice and clears the rest, so switching to single can never
// leave an illegal two-key state behind. AllowMultipleAnswers only widens the
// rule, so it touches no `correct` flag.
export type AllowSingleAnswer = {
    readonly type: "allow-single-answer";
    readonly mcqId: ElementId;
};
export type AllowMultipleAnswers = {
    readonly type: "allow-multiple-answers";
    readonly mcqId: ElementId;
};

// --- Block ops (reshape the element array itself) ---
export type InsertElement = {
    readonly type: "insert-element";
    readonly element: Element; // fresh id minted at the dispatch site, passed in
    readonly index: number;
};
export type RemoveElement = {
    readonly type: "remove-element";
    readonly id: ElementId;
};
export type MoveElement = {
    readonly type: "move-element";
    readonly id: ElementId;
    readonly toIndex: number;
};

// A block "turns into" a different kind at the SAME position, keeping its id —
// the markdown-rule / turn-into transform (document-first-editor.md §3, §4.2).
// `to` is the WHOLE destination element, built by the caller (never assembled
// here) so the fold stays a one-line id-keyed replace; `proseToHeading` /
// `headingToProse` below are the ONE place that construction happens, which is
// what keeps an illegal state (e.g. a heading missing `level`) unrepresentable.
// Id-stability is the caller's invariant to uphold (`to.id === id`) — the fold
// does not re-check it (checking would mean minting a second source of truth
// for "the id", which is exactly the redundancy the rule is meant to prevent).
export type RetypeElement = {
    readonly type: "retype-element";
    readonly id: ElementId;
    readonly to: Element;
};

export type EditCommand =
    | SetHeadingText
    | SetProseMarkdown
    | SetObjectiveText
    | SetMcqStem
    | SetChoiceText
    | SetChoiceFeedback
    | AddChoice
    | RemoveChoice
    | MoveChoice
    | MarkChoiceCorrect
    | UnmarkChoiceCorrect
    | SetHeadingLevel
    | AllowSingleAnswer
    | AllowMultipleAnswers
    | InsertElement
    | RemoveElement
    | MoveElement
    | RetypeElement;

// === The fold ================================================================
// `applyEdit(elements)(cmd)` — total, pure, data-last (curried so it composes in
// a fold/reduce). Field & choice commands map the matching element by id; block
// commands reshape the array. Every arm returns a fresh array; inputs are never
// mutated. Unknown id ⇒ the tree is returned unchanged.

const applyEdit =
    (elements: readonly Element[]) =>
    (cmd: EditCommand): readonly Element[] => {
        switch (cmd.type) {
            case "set-heading-text":
                return elements.map((e) =>
                    e.id === cmd.id && e.type === "heading"
                        ? { ...e, text: cmd.text }
                        : e,
                );
            case "set-prose-markdown":
                return elements.map((e) =>
                    e.id === cmd.id && e.type === "prose"
                        ? { ...e, markdown: cmd.markdown }
                        : e,
                );
            case "set-objective-text":
                return elements.map((e) =>
                    e.id === cmd.id && e.type === "objective"
                        ? { ...e, text: cmd.text }
                        : e,
                );
            case "set-mcq-stem":
                return elements.map((e) =>
                    e.id === cmd.id && e.type === "mcq"
                        ? { ...e, stem: cmd.stem }
                        : e,
                );
            case "set-choice-text":
                return mapMcqChoices(elements, cmd.mcqId, (choices) =>
                    choices.map((c) =>
                        c.id === cmd.choiceId ? { ...c, text: cmd.text } : c,
                    ),
                );
            case "set-choice-feedback":
                return mapMcqChoices(elements, cmd.mcqId, (choices) =>
                    choices.map((c) =>
                        c.id === cmd.choiceId
                            ? { ...c, feedback: cmd.feedback }
                            : c,
                    ),
                );
            case "add-choice":
                return mapMcqChoices(elements, cmd.mcqId, (choices) => [
                    ...choices,
                    cmd.choice,
                ]);
            case "remove-choice":
                return mapMcqChoices(elements, cmd.mcqId, (choices) =>
                    choices.filter((c) => c.id !== cmd.choiceId),
                );
            case "mark-choice-correct":
                // On a single-answer MCQ, making one choice the key clears the
                // others; on a multiple-answer MCQ the others are untouched.
                return mapMcq(elements, cmd.mcqId, (mcq) => ({
                    ...mcq,
                    choices: mcq.choices.map((c) => {
                        if (c.id === cmd.choiceId)
                            return { ...c, correct: true };
                        return mcq.answerMode === "single"
                            ? { ...c, correct: false }
                            : c;
                    }),
                }));
            case "unmark-choice-correct":
                return mapMcqChoices(elements, cmd.mcqId, (choices) =>
                    choices.map((c) =>
                        c.id === cmd.choiceId ? { ...c, correct: false } : c,
                    ),
                );
            case "move-choice":
                // Reorder within the MCQ's choice list: remove the choice, then
                // splice it back at `toIndex` (index into the without-list) —
                // the exact toIndex semantics of `move-element` below.
                return mapMcqChoices(elements, cmd.mcqId, (choices) => {
                    const from = choices.findIndex(
                        (c) => c.id === cmd.choiceId,
                    );
                    if (from === -1) return choices; // unknown id ⇒ no-op
                    const moved = choices[from];
                    if (moved === undefined) return choices;
                    const without = [
                        ...choices.slice(0, from),
                        ...choices.slice(from + 1),
                    ];
                    return [
                        ...without.slice(0, cmd.toIndex),
                        moved,
                        ...without.slice(cmd.toIndex),
                    ];
                });
            case "set-heading-level":
                return elements.map((e) =>
                    e.id === cmd.id && e.type === "heading"
                        ? { ...e, level: cmd.level }
                        : e,
                );
            case "allow-single-answer":
                // Switching to single narrows the invariant to "at most one key":
                // keep the FIRST correct choice, clear every later one, and set
                // the mode — so no illegal two-key state survives the switch. The
                // one key kept is the first correct choice's id (undefined ⇒ none
                // were correct, and the map clears nothing).
                return mapMcq(elements, cmd.mcqId, (mcq) => {
                    const keyId = mcq.choices.find((c) => c.correct)?.id;
                    return {
                        ...mcq,
                        answerMode: "single",
                        choices: mcq.choices.map((c) =>
                            c.correct && c.id !== keyId
                                ? { ...c, correct: false }
                                : c,
                        ),
                    };
                });
            case "allow-multiple-answers":
                // Widening the rule touches no `correct` flag.
                return mapMcq(elements, cmd.mcqId, (mcq) => ({
                    ...mcq,
                    answerMode: "multiple",
                }));
            case "insert-element":
                // slice tolerates out-of-range indices (clamps to the ends).
                return [
                    ...elements.slice(0, cmd.index),
                    cmd.element,
                    ...elements.slice(cmd.index),
                ];
            case "remove-element":
                return elements.filter((e) => e.id !== cmd.id);
            case "move-element": {
                const from = elements.findIndex((e) => e.id === cmd.id);
                if (from === -1) return elements; // unknown id ⇒ no-op
                const moved = elements[from];
                const without = [
                    ...elements.slice(0, from),
                    ...elements.slice(from + 1),
                ];
                return [
                    ...without.slice(0, cmd.toIndex),
                    moved,
                    ...without.slice(cmd.toIndex),
                ];
            }
            case "retype-element":
                return elements.map((e) => (e.id === cmd.id ? cmd.to : e));
            default:
                return exhaustSwitchCase(cmd);
        }
    };

// --- Internal MCQ helpers (keep the choice-command arms one-liners) ---
// Both are pure and total: a missing mcqId, or an element that is not an MCQ,
// leaves the tree unchanged.
const mapMcq = (
    elements: readonly Element[],
    mcqId: ElementId,
    f: (mcq: Extract<Element, { type: "mcq" }>) => Element,
): readonly Element[] =>
    elements.map((e) => (e.id === mcqId && e.type === "mcq" ? f(e) : e));

const mapMcqChoices = (
    elements: readonly Element[],
    mcqId: ElementId,
    f: (choices: readonly Choice[]) => readonly Choice[],
): readonly Element[] =>
    mapMcq(elements, mcqId, (mcq) => ({ ...mcq, choices: f(mcq.choices) }));

export { applyEdit };

// === Set text by id (the manual inline-edit dispatch) =========================
// Resolve an ElementId to WHICHEVER text field it names — a top-level element's
// primary text (heading / objective / prose markdown / mcq stem) or a choice's
// text inside some MCQ — and set it, composing the matching `applyEdit` command
// so every mutation still flows through the one fold. Total and pure: an id that
// matches nothing (unknown id) returns the tree unchanged.
//
// This is the local optimistic apply behind a MANUAL text edit: the caller has
// only the edited element's id (the same id it feeds to the `updateElement`
// port), not the element kind — so this picks the right typed command by
// locating the id. It is NOT a meaning-swapping flag: each branch dispatches a
// distinct, named command; the branch is on the element's CONTENT (its kind and
// where the id lives), exactly like `applyValueMatch` below.
const setElementText = (
    elements: readonly Element[],
    id: ElementId,
    text: string,
): readonly Element[] => {
    const element = elements.find((e) => e.id === id);
    if (element !== undefined) {
        switch (element.type) {
            case "heading":
                return applyEdit(elements)({
                    type: "set-heading-text",
                    id,
                    text,
                });
            case "objective":
                return applyEdit(elements)({
                    type: "set-objective-text",
                    id,
                    text,
                });
            case "prose":
                return applyEdit(elements)({
                    type: "set-prose-markdown",
                    id,
                    markdown: text,
                });
            case "mcq":
                return applyEdit(elements)({
                    type: "set-mcq-stem",
                    id,
                    stem: text,
                });
            default:
                return exhaustSwitchCase(element);
        }
    }
    // Not a top-level element: the id may name a choice inside an MCQ.
    const owner = elements.find(
        (e) => e.type === "mcq" && e.choices.some((c) => c.id === id),
    );
    if (owner !== undefined && owner.type === "mcq")
        return applyEdit(elements)({
            type: "set-choice-text",
            mcqId: owner.id,
            choiceId: id,
            text,
        });
    return elements;
};

export { setElementText };

// === Retype builders (the ONE place a destination element is constructed) =====
// A "turn into" transform needs a WHOLE new element built from the old one —
// unlike every other command above, whose payload is a scalar the fold slots
// straight in. Building that element is kept OUT of the fold and out of any
// call site: these two functions are the only place it happens, so a heading's
// `level` (required by its type) can never go missing and a prose block's
// `markdown` can never leak a stale `level` field. Both carry the id through
// unchanged (id-stability: a retype never re-anchors the block).
//
// `proseToHeading` takes an explicit `level` (default 1) rather than inferring
// one from the prose text, because the two callers that need it know it two
// different ways: a markdown rule reads it off the typed `#`/`##`/`###` run
// (inputRules.ts), while a manual "turn into heading" pick has no such signal
// and means "just make it a heading" — level 1 is the honest default for that
// case, and the author can change it after via `onSetHeadingLevel`.
const proseToHeading = (
    prose: ProseElement,
    level: 1 | 2 | 3 = 1,
): HeadingElement => ({
    type: "heading",
    id: prose.id,
    level,
    text: prose.markdown,
});

// The inverse: a heading's `level` has no home on a ProseElement, so it is
// dropped (not carried as a stray field) — a prose block never has a level.
const headingToProse = (heading: HeadingElement): ProseElement => ({
    type: "prose",
    id: heading.id,
    markdown: heading.text,
});

export { headingToProse, proseToHeading };

// === Retype an element by id (the manual "turn into" dispatch) ================
// The twin of `setElementText`: the caller has only the target's id (a toolbar
// "turn into heading" pick, say) plus which kind it should become. Resolves the
// id, narrows the CURRENT element, and — only for a kind pair this function
// knows how to convert — builds the destination via the matching builder above
// and dispatches `retype-element`. Total and pure: an unknown id, an already-
// matching kind, or an unsupported pair (anything but prose⇄heading, for now)
// is a no-op, never a throw.
const retypeElement = (
    elements: readonly Element[],
    id: ElementId,
    toKind: "prose" | "heading",
): readonly Element[] => {
    const element = elements.find((e) => e.id === id);
    if (element === undefined) return elements; // unknown id ⇒ no-op
    if (toKind === "heading" && element.type === "prose")
        return applyEdit(elements)({
            type: "retype-element",
            id,
            to: proseToHeading(element),
        });
    if (toKind === "prose" && element.type === "heading")
        return applyEdit(elements)({
            type: "retype-element",
            id,
            to: headingToProse(element),
        });
    return elements; // already that kind, or an unsupported pair ⇒ no-op
};

export { retypeElement };

// === Set choice feedback by id (the manual feedback-edit dispatch) ============
// Resolve a choice id to the MCQ that owns it and set that choice's feedback,
// composing the `set-choice-feedback` command so the mutation stays in the one
// fold. Total and pure: a choiceId that belongs to no MCQ (unknown id) returns
// the tree unchanged. This is the local optimistic apply behind a MANUAL
// feedback edit — the twin of `setElementText`, but routed to feedback (which
// persists via `set_element_attrs`, not `set_element_text`).
const setChoiceFeedback = (
    elements: readonly Element[],
    choiceId: ElementId,
    feedback: string,
): readonly Element[] => {
    const owner = elements.find(
        (e) => e.type === "mcq" && e.choices.some((c) => c.id === choiceId),
    );
    if (owner !== undefined && owner.type === "mcq")
        return applyEdit(elements)({
            type: "set-choice-feedback",
            mcqId: owner.id,
            choiceId,
            feedback,
        });
    return elements;
};

export { setChoiceFeedback };

// === Mark / unmark a choice correct by id (the manual correctness dispatch) ===
// Resolve a choice id to the MCQ that owns it and dispatch the matching
// correctness command through the one fold. Total and pure: a choiceId that
// belongs to no MCQ (unknown id) returns the tree unchanged. Twins of
// `setChoiceFeedback` — the by-id dispatch behind the renderer's radio, which
// carries only the choice id. Two functions, never a setCorrect(boolean) flag:
// each routes to its own named command. `markChoiceCorrect` clears the siblings
// on a single-answer MCQ (the "exactly one key" invariant lives in that fold),
// so the host reads the pre/post trees to learn which siblings it must persist.
const markChoiceCorrect = (
    elements: readonly Element[],
    choiceId: ElementId,
): readonly Element[] => {
    const owner = elements.find(
        (e) => e.type === "mcq" && e.choices.some((c) => c.id === choiceId),
    );
    if (owner !== undefined && owner.type === "mcq")
        return applyEdit(elements)({
            type: "mark-choice-correct",
            mcqId: owner.id,
            choiceId,
        });
    return elements;
};

const unmarkChoiceCorrect = (
    elements: readonly Element[],
    choiceId: ElementId,
): readonly Element[] => {
    const owner = elements.find(
        (e) => e.type === "mcq" && e.choices.some((c) => c.id === choiceId),
    );
    if (owner !== undefined && owner.type === "mcq")
        return applyEdit(elements)({
            type: "unmark-choice-correct",
            mcqId: owner.id,
            choiceId,
        });
    return elements;
};

export { markChoiceCorrect, unmarkChoiceCorrect };

// === Reorder a choice by id (the manual choice-reorder dispatch) ==============
// Resolve a choice id to the MCQ that owns it and reorder it to `toIndex` in that
// MCQ's choice list, composing `move-choice` so the mutation stays in the one
// fold. Total and pure: a choiceId that belongs to no MCQ (unknown id) returns
// the tree unchanged. The renderer's up/down controls compute `toIndex` from the
// choice's current position (± 1); this routes it to the owning MCQ.
const moveChoice = (
    elements: readonly Element[],
    choiceId: ElementId,
    toIndex: number,
): readonly Element[] => {
    const owner = elements.find(
        (e) => e.type === "mcq" && e.choices.some((c) => c.id === choiceId),
    );
    if (owner !== undefined && owner.type === "mcq")
        return applyEdit(elements)({
            type: "move-choice",
            mcqId: owner.id,
            choiceId,
            toIndex,
        });
    return elements;
};

export { moveChoice };

// === anchorForMove — toIndex → domain sibling-anchor + position ===============
// The UI move commands (`move-element`, `move-choice`) speak `toIndex` — an index
// into the sibling list with the moved item removed. The DOMAIN move primitive
// (`move_element`) speaks sibling-relative: land BEFORE / AFTER a named anchor.
// This pure function translates the former into the latter so the host can
// persist a reorder through the same primitive the agent uses. Landing at an
// index that still has a sibling ⇒ "before that sibling"; landing past the end ⇒
// "after the last sibling". Returns undefined when there is no other sibling to
// anchor to (a one-item list — the move is a no-op with nothing to persist).
type MoveAnchor = {
    readonly anchorId: ElementId;
    readonly position: "before" | "after";
};

const anchorForMove = (
    siblings: readonly { readonly id: ElementId }[],
    movingId: ElementId,
    toIndex: number,
): MoveAnchor | undefined => {
    const without = siblings.filter((s) => s.id !== movingId);
    if (without.length === 0) return undefined; // nothing to anchor to
    const clamped = Math.max(0, Math.min(toIndex, without.length));
    if (clamped < without.length) {
        const anchor = without[clamped];
        return anchor === undefined
            ? undefined
            : { anchorId: anchor.id, position: "before" };
    }
    const last = without[without.length - 1];
    return last === undefined
        ? undefined
        : { anchorId: last.id, position: "after" };
};

export type { MoveAnchor };
export { anchorForMove };

// === Accepted-proposal → content change ======================================
// Translate an ACCEPTED review outcome into the actual content change, returning
// the new tree. Composes `applyEdit` for every mutation.
//
// VALUE-MATCH strategy (INTERIM). A scalar-edit / list-edit op carries only a
// localised `field` LABEL plus opaque `before`/`after` STRINGS — it does not yet
// carry a machine field path (that is a later slice, once edit ops travel with a
// typed path). So to apply the change we locate the target element by id and set
// whichever of its string fields CURRENTLY equals `op.before` to `op.after`. It
// covers the mock's stem-edit (fixtures.ts `stemEditProposal`) and every other
// single-string field: mcq stem, each choice text, heading text, prose markdown,
// objective text. When a proper field path lands, this value-match goes away.
//
// Only the ACCEPT outcomes touch content:
//   • use-new / keep-both on a scalar-edit / list-edit → value-match apply.
//     (keep-both is list-valued-combine in the domain; with the interim string
//     model there is no separate list cell to append to, so it applies the new
//     value — same effect as use-new for these single-string fields.)
//   • remove on a remove op → RemoveElement.
// Everything else returns the tree unchanged:
//   • reject outcomes: keep-current / skip / keep-it / keep-order.
//   • accept outcomes with no string change to apply here: add (no element
//     payload on the op), apply-move (no indices on the op).
//   • inline-edit ops, whose before/after are RichText trees, not strings.

const applyAcceptedProposal = (
    elements: readonly Element[],
    elementId: ElementId,
    proposal: PendingProposal,
    outcome: ReviewOutcome,
): readonly Element[] => {
    switch (outcome.type) {
        case "use-new":
        case "keep-both": {
            const op = proposal.op;
            if (op.type !== "scalar-edit" && op.type !== "list-edit")
                return elements; // non-applicable op (add/remove/move/inline)
            return applyValueMatch(elements, elementId, op.before, op.after);
        }
        case "remove":
            if (proposal.op.type !== "remove") return elements;
            return applyEdit(elements)({
                type: "remove-element",
                id: elementId,
            });
        // Reject outcomes and accept-outcomes with no string change here.
        case "keep-current":
        case "skip":
        case "keep-it":
        case "keep-order":
        case "add":
        case "apply-move":
            return elements;
        default:
            return exhaustSwitchCase(outcome);
    }
};

// Find the element by id and set whichever string field currently holds `before`
// to `after`, expressed as an `applyEdit` command so the mutation stays in one
// place. A miss at any level (unknown id, no field matches `before`) is a no-op.
const applyValueMatch = (
    elements: readonly Element[],
    elementId: ElementId,
    before: string,
    after: string,
): readonly Element[] => {
    const element = elements.find((e) => e.id === elementId);
    if (element === undefined) return elements;
    switch (element.type) {
        case "heading":
            return element.text === before
                ? applyEdit(elements)({
                      type: "set-heading-text",
                      id: elementId,
                      text: after,
                  })
                : elements;
        case "prose":
            return element.markdown === before
                ? applyEdit(elements)({
                      type: "set-prose-markdown",
                      id: elementId,
                      markdown: after,
                  })
                : elements;
        case "objective":
            return element.text === before
                ? applyEdit(elements)({
                      type: "set-objective-text",
                      id: elementId,
                      text: after,
                  })
                : elements;
        case "mcq": {
            if (element.stem === before)
                return applyEdit(elements)({
                    type: "set-mcq-stem",
                    id: elementId,
                    stem: after,
                });
            const choice = element.choices.find((c) => c.text === before);
            if (choice === undefined) return elements;
            return applyEdit(elements)({
                type: "set-choice-text",
                mcqId: elementId,
                choiceId: choice.id,
                text: after,
            });
        }
        default:
            return exhaustSwitchCase(element);
    }
};

export { applyAcceptedProposal };
